import common from "../common";
import GelButton from "./gel/GelButton";
import GelHackButton from "./gel/GelHackButton";
import moment from "moment";

export default class LocationSelector extends PIXI.Container {
    constructor(finishedTraining = false, spacing = 54) {
        super();

        this.signals = {
            select: new P3.utils.MiniSignal()
        };

        this._finishedTraining = finishedTraining;
        this._spacing = spacing;
        this._xpos = 0;
        this._index = 0;
        this._wrapIndex = 0;
        this._snapIndex = 0;
        this._locations = [];

        this._holder = new PIXI.Container();
        this.addChild(this._holder);

        this._initLocations();

        this._nextButton = GelHackButton.create(
            P3.assets.texture("ui_btn_chevron_l_bg_normal"),
            P3.assets.texture("ui_btn_chevron_l_over"),
            P3.assets.texture("ui_btn_chevron_l_bg_down"),
            P3.assets.texture("ui_icon_chevron_r_out")
        );
        this._nextButton.position.set(224, 0);
        this._nextButton.animate = false;
        this._nextButton.animateIcon = false;
        this._nextButton.clickSoundName = "sfx_btn_press_00";
        this._nextButton.tabIndex = GelButton.TAB_INDEX_LEVEL_1 + 1;
        this._nextButton.refocusable = true;
        this._nextButton.visible = this._locations.length > 1;
        this._nextButton.signals.click.add(this.onNextButtonClick, this);
        this.addChild(this._nextButton);

        this._previousButton = GelHackButton.create(
            P3.assets.texture("ui_btn_chevron_r_bg_normal"),
            P3.assets.texture("ui_btn_chevron_r_over"),
            P3.assets.texture("ui_btn_chevron_r_bg_down"),
            P3.assets.texture("ui_icon_chevron_l_out")
        );
        this._previousButton.position.set(-224, 0);
        this._previousButton.animate = false;
        this._previousButton.animateIcon = false;
        this._previousButton.clickSoundName = "sfx_btn_press_00";
        this._previousButton.tabIndex = GelButton.TAB_INDEX_LEVEL_1;
        this._previousButton.refocusable = true;
        this._previousButton.visible = this._locations.length > 1;
        this._previousButton.signals.click.add(this.onPreviousButtonClick, this);
        this.addChild(this._previousButton);

        this.on("added", this.init, this);
    }

    init() {
        this.off("added", this.init, this);

        this.scroll(0);
    }

    destroy() {
        P3.utils.destroyAllSignals(this.signals);

        super.destroy({children: true});
    }

    update(dt) {
        const ease = 8;
        this.scroll((this._xpos - (this._spacing * this._snapIndex)) * ease * dt);
    }

    scroll(x) {
        this._xpos -= x;
        this._wrapIndex = Math.floor((this._xpos + this._spacing * 0.5) / this._spacing);
        this._index = this._wrapIndex;

        const l = this._locations.length;
        this._index %= l;
        if (this._index < 0) {
            this._index += l;
        }

        for (let i = 0; i < this._locations.length; ++i) {
            const location = this._locations[i];
            location.parent.x += x;

            while (location.parent.x > this._spacing * this._locations.length * 0.5)
                location.parent.x -= this._spacing * this._locations.length;

            while (location.parent.x < -this._spacing * this._locations.length * 0.5)
                location.parent.x += this._spacing * this._locations.length;

            location.scale.set(P3.math.clamp(1 - (Math.abs(location.parent.x * 0.4) / 240), 0, 1));
            location.alpha = P3.math.clamp(Math.pow(1 - ((Math.abs(location.parent.x) - 20) / 200), 3), 0, 1);
        }

        this._holder.children.sort((a, b) => {
            return Math.abs(b.x) - Math.abs(a.x);
        });
    }

    scrollTo(index) {
        this.scroll(this._xpos - this._locations[index].parent.x);
        this._snapIndex = this._wrapIndex;

        const challenge = this._locations[this.selected].challenge;
        if (challenge)
        {
            if (challenge.tween)
                challenge.tween.stop();

            challenge.y = 274 - challenge.height;
            challenge.tween = new P3.utils.TWEEN.Tween(challenge, this._tweens)
                .to({y: 274}, 240)
                .easing(P3.utils.TWEEN.Easing.Quadratic.Out)
                .delay(200)
                .onStart(() => challenge.visible = true)
                .start();
        }
    }

    next() {
        let challenge = this._locations[this.selected].challenge;
        if (challenge)
        {
            challenge.visible = false;

            if (challenge.tween)
                challenge.tween.stop();
        }

        ++this._snapIndex;

        const id = this._locations[this.selected].id;
        this.signals.select.dispatch(this, id, this.selected);

        challenge = this._locations[this.selected].challenge;
        if (challenge)
        {
            if (challenge.tween)
                challenge.tween.stop();

            challenge.y = 274 - challenge.height;
            challenge.tween = new P3.utils.TWEEN.Tween(challenge, this._tweens)
                .to({y: 274}, 240)
                .easing(P3.utils.TWEEN.Easing.Quadratic.Out)
                .delay(200)
                .onStart(() => challenge.visible = true)
                .start();
        }

        P3.audio.playSound("sfx_level_select_card_00");
    }

    previous() {
        let challenge = this._locations[this.selected].challenge;
        if (challenge)
        {
            challenge.visible = false;

            if (challenge.tween)
                challenge.tween.stop();
        }

        --this._snapIndex;

        const id = this._locations[this.selected].id;
        this.signals.select.dispatch(this, id, this.selected);

        challenge = this._locations[this.selected].challenge;
        if (challenge)
        {
            if (challenge.tween)
                challenge.tween.stop();

            challenge.y = 274 - challenge.height;
            challenge.tween = new P3.utils.TWEEN.Tween(challenge, this._tweens)
                .to({y: 274}, 240)
                .easing(P3.utils.TWEEN.Easing.Quadratic.Out)
                .delay(200)
                .onStart(() => challenge.visible = true)
                .start();
        }

        P3.audio.playSound("sfx_level_select_card_00");
    }

    createLocation(name, textures, level = 0, training = false, challenge = false) {
        const card = new PIXI.Container();

        card.base = new PIXI.Sprite(textures.base);
        card.base.anchor.set(0.5);
        card.addChild(card.base);

        card.image = new PIXI.Sprite(textures.card);
        card.image.position.set(0, 16);
        card.image.anchor.set(0.5);
        card.addChild(card.image);

        card.label = new PIXI.extras.BitmapText(name, {font: "28px BBC Reith Sans", align: "center"});
        card.label.position.set(0, -194);
        card.label.anchor.set(0.5);
        card.label.tint = 0x2B365A;
        card.label.maxWidth = 320;
        card.addChild(card.label);

        const count = !training ? 4 : 1;
        const spacing = 52;

        card.icons = new PIXI.Container();
        card.icons.y = 160;
        card.icons.pivot.x = spacing * (count - 1) * 0.5;
        card.addChild(card.icons);

        if (challenge)
        {
            if (common.save.isWeeklyChallengeComplete(common.userId))
            {
                card.challenge = new PIXI.Sprite(P3.assets.texture("ui_card_new_challenge"));
                card.challenge.position.set(0, 274 - card.challenge.height);
                card.challenge.anchor.set(0.5);
                card.challenge.visible = false;
                card.addChildAt(card.challenge, 0);

                card.challenge.text = new PIXI.extras.BitmapText("New challenge in " + (7 - common.weekday) + " days", {font: "23px BBC Reith Sans", align: "center"});
                card.challenge.text.position.set(0, -8);
                card.challenge.text.anchor.set(0.5);
                card.challenge.addChild(card.challenge.text);
            }
        }

        const user = common.save.getUserData(common.userId);

        for (let i = 0; i < count; ++i) {
            const icon = new PIXI.Sprite(textures.shieldOff);
            icon.position.set(i * spacing, 0);
            icon.anchor.set(0.5);
            card.icons.addChild(icon);

            icon.on = new PIXI.Sprite(textures.shieldOn);
            icon.on.anchor.set(0.5);
            icon.on.visible = (training && user.finishedTraining) ? true : i < level;
            icon.addChild(icon.on);
        }
        return card;
    }

    disableInput(value) {
        this._nextButton.interactive = !value;
        this._previousButton.interactive = !value;
    }

    _initLocations() {
        const locations = P3.utils.objectToArray(P3.assets.json("locations")).filter(
            x => !this._finishedTraining ? x.enabled && x.training : x.enabled);

        locations.sort((a, b) => {return a.order - b.order});

        for (let i = 0; i < locations.length; ++i) {
            const t = locations[i];

            console.log(t);

            const holder = new PIXI.Container();
            holder.position.set(i * this._spacing, 0);
            this._holder.addChild(holder);

            const level = common.difficulty.getLevelUnclamped(t);
            const location = this.createLocation(t.name, {
                    base: P3.assets.texture(t.images.base),
                    card: P3.assets.texture(t.images.card),
                    shieldOn: P3.assets.texture(t.images.shield_on),
                    shieldOff: P3.assets.texture(t.images.shield_off),
                },
                level,
                t.training,
                t.challenge);

            location.id = t.id;
            holder.addChild(location);

            this._locations.push(location);
        }
    }

    onNextButtonClick(button) {
        this.next();
    }

    onPreviousButtonClick(button) {
        this.previous();
    }

    get selected() {
        const l = this._locations.length;
        let index = this._snapIndex;
        index %= l;
        if (index < 0) {
            index += l;
        }
        return index;
    }

    get nextButton() {
        return this._nextButton;
    }

    get previousButton() {
        return this._previousButton;
    }
}